import pick from 'lodash/pick'
import { initSession } from 'roadie-ui'

const SessionInfoType = {
  profile_id: 0 as number,
  guid: '' as string,
  access_token: '' as string,
  access_token_valid_until: '' as string,
  refresh_token: '' as string,
  refresh_token_valid_until: '' as string,
  user_identity_guid: '' as Maybe<string>,
  current_profile_guid: '' as Maybe<string>,
} as const

export type SessionInfo = typeof SessionInfoType
const sessionInfoKeys = Object.keys(SessionInfoType) as (keyof SessionInfo)[]

function pluckSessionProps(sessionInfo: SessionInfo) {
  // Goal is to remove any additional info, we don't want to cache.
  return pick(sessionInfo, sessionInfoKeys)
}

const LOCAL_STORAGE_NAME = 'access-token'
const LOGGED_OUT_KEY = 'was-logged-out'

export function isEphemeral() {
  return !!sessionStorage.getItem(LOCAL_STORAGE_NAME)
}

function getStorage(prefer?: 'local' | 'session') {
  if (prefer && prefer === 'local') {
    // if prefers local storage, return local storage
    return localStorage
  }
  if (prefer && prefer === 'session') {
    // if prefers session storage, return session storage
    return sessionStorage
  }
  if (isEphemeral()) {
    // if session storage already being used, continue using it.
    return sessionStorage
  }
  // default to local storage.
  return localStorage
}

export function getSessionInfo() {
  const str = getStorage().getItem(LOCAL_STORAGE_NAME)
  if (str) {
    return JSON.parse(str) as SessionInfo
  }
}

export function cacheSessionInfo(sessionInfo: SessionInfo, ephemeral = false) {
  const pickedSessionData = pluckSessionProps(sessionInfo)
  const storage = getStorage(ephemeral ? 'session' : 'local')
  storage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(pickedSessionData))
}

export function patchSessionInfo(partialSessionData: Partial<SessionInfo>) {
  const prevData = getSessionInfo()
  if (!prevData) {
    throw new Error('No Existing Session To Patch')
  }
  const nextData: SessionInfo = { ...prevData, ...partialSessionData }
  cacheSessionInfo(nextData)
  return nextData
}

function expired(str: string) {
  return Date.now() > new Date(str).getTime()
}

export function isActive() {
  const session = getSessionInfo()
  if (!session) {
    return false
  }
  if (!expired(session.access_token_valid_until)) {
    return true
  }
  if (!expired(session.refresh_token_valid_until)) {
    return true
  }
  return false
}

export function destroySessionInfo(message = 'You have been signed out.') {
  getStorage().removeItem(LOCAL_STORAGE_NAME)
  getStorage('local').setItem(LOGGED_OUT_KEY, message)
  window.location.reload()
}

export function wasSignedOut() {
  const message = getStorage('local').getItem(LOGGED_OUT_KEY)
  getStorage('local').removeItem(LOGGED_OUT_KEY)
  return message
}

export function initRoadieUISession() {
  initSession({
    getSessionInfo,
    cacheSessionInfo,
    patchSessionInfo,
    destroySessionInfo,
  })
}
